import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid2 as Grid, Alert, CircularProgress, Chip, Paper,
  Table, TableHead, TableBody, TableRow, TableCell, Button, Dialog, DialogTitle, DialogContent,
  DialogActions,
} from '@mui/material';
import { Campaign, Refresh, Send } from '@mui/icons-material';
import api from '../../../services/api';

/**
 * Leads de entidades — quem recebeu o follow-up «O que quer fazer com a Pagali?»
 * no dia seguinte ao registo, e que serviços marcou. Ver lead-entidade.service.ts.
 */
interface Interesse { servico: string; nome: string; origem: string; created_at: string }
interface Lead {
  user_id: string; email: string; nome: string; subtipo: string | null;
  followup_enviado_em: string | null; followup_erro: string | null; interesse_em: string | null;
  created_at: string; interesses: Interesse[];
}

const SUBTIPOS: Record<string, string> = {
  PFLOJ: 'Loja/Comércio', PFEMP: 'Empresa/Serviços', PFGOV: 'Governo', PFONG: 'ONG/Associação',
  PFEVT: 'Promotor de Eventos', PFPRO: 'Profissional Liberal', PFTUR: 'Promotor Turístico',
};

const dataCV = (v: string | null) => v
  ? new Date(v).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde', dateStyle: 'short', timeStyle: 'short' })
  : '—';

function etapa(l: Lead): { label: string; color: 'default' | 'info' | 'success' | 'error' } {
  if (l.followup_erro && !l.followup_enviado_em) return { label: 'Email falhou', color: 'error' };
  if (l.interesses.length) return { label: 'Interessado', color: 'success' };
  if (l.followup_enviado_em) return { label: 'Contactado', color: 'info' };
  return { label: 'Pendente', color: 'default' };
}

const LeadsEntidades: React.FC = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previa, setPrevia] = useState<any | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get('/admin/leads-entidades');
      setLeads(r.data.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Erro ao carregar');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Primeiro mostra a quem vai sair (dryRun); só envia depois de confirmar.
  const verPrevia = async () => {
    setError(null);
    try {
      const r = await api.post('/admin/leads-entidades/enviar', { dryRun: true });
      setPrevia(r.data.data);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message);
    }
  };

  const enviar = async () => {
    setEnviando(true);
    try {
      const r = await api.post('/admin/leads-entidades/enviar', { dryRun: false });
      setAviso(`Enviados ${r.data.data.enviados}, falhados ${r.data.data.falhados}.`);
      setPrevia(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setEnviando(false);
    }
  };

  const contactados = leads.filter((l) => l.followup_enviado_em).length;
  const interessados = leads.filter((l) => l.interesses.length).length;
  const tiles = [
    { label: 'Entidades contactadas', value: contactados },
    { label: 'Com interesse', value: interessados, color: 'success.main' },
    { label: 'Taxa de resposta', value: contactados ? `${Math.round((interessados / contactados) * 100)}%` : '—' },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
        <Campaign sx={{ color: 'primary.main' }} />
        <Typography variant="h5" fontWeight={700} sx={{ flex: 1 }}>Leads de Entidades</Typography>
        <Button variant="outlined" startIcon={<Refresh />} onClick={load}>Atualizar</Button>
        <Button variant="contained" startIcon={<Send />} onClick={verPrevia}>Enviar follow-up agora</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Entidades novas recebem no dia seguinte ao registo o email «O que quer fazer com a Pagali?» (diário, 10h30).
        Cada interesse marcado avisa admin@pagali.cv e o WhatsApp comercial.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {aviso && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAviso(null)}>{aviso}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {tiles.map((t) => (
          <Grid key={t.label} size={{ xs: 12, sm: 4 }}>
            <Card><CardContent sx={{ py: 2 }}>
              <Typography variant="caption" color="text.secondary">{t.label}</Typography>
              <Typography variant="h5" fontWeight={700} sx={{ color: t.color }}>{t.value}</Typography>
            </CardContent></Card>
          </Grid>
        ))}
      </Grid>

      <Paper variant="outlined">
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Entidade</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Follow-up</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Etapa</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Interesses</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leads.length === 0 && (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Ainda não há entidades contactadas.
                  </TableCell></TableRow>
                )}
                {leads.map((l) => {
                  const e = etapa(l);
                  return (
                    <TableRow key={l.user_id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{l.nome}</Typography>
                        <Typography variant="caption" color="text.secondary">{l.email}</Typography>
                      </TableCell>
                      <TableCell>{SUBTIPOS[l.subtipo || ''] || l.subtipo || '—'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{dataCV(l.followup_enviado_em)}</TableCell>
                      <TableCell><Chip size="small" color={e.color} label={e.label} title={l.followup_erro || ''} /></TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {l.interesses.map((i) => <Chip key={i.servico} size="small" variant="outlined" label={i.nome} />)}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>

      <Dialog open={!!previa} onClose={() => setPrevia(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Enviar follow-up agora</DialogTitle>
        <DialogContent>
          {previa?.candidatos ? (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {previa.candidatos} entidade(s) registada(s) há 20 a 72 horas ainda sem follow-up:
              </Typography>
              {previa.lista.map((c: any) => (
                <Typography key={c.email} variant="body2">• {c.nome} — {c.email}</Typography>
              ))}
            </>
          ) : (
            <Typography variant="body2">Não há entidades por contactar neste momento.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrevia(null)}>Cancelar</Button>
          <Button variant="contained" disabled={!previa?.candidatos || enviando} onClick={enviar}>
            {enviando ? 'A enviar…' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeadsEntidades;
